import { useState, useRef, useEffect } from 'react'
import {
  useFormContext,
  Controller,
  type RegisterOptions,
} from 'react-hook-form'
import { LuChevronDown, LuSearch, LuCheck } from 'react-icons/lu'
import { cn } from '#/lib/utils'

export type ComboboxOption = { value: string; label: string; sublabel?: string }

type FormComboboxProps = {
  name: string
  label: string
  placeholder?: string
  searchPlaceholder?: string
  options: ComboboxOption[]
  rules?: RegisterOptions
  className?: string
  icon?: React.ReactNode
  variant?: 'light' | 'dark'
}

export function FormCombobox({
  name,
  label,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  options,
  rules,
  className,
  icon,
  variant = 'light',
}: FormComboboxProps) {
  const { control, trigger, clearErrors } = useFormContext()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isLight = variant === 'light'

  const filtered = query.trim()
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(query.toLowerCase()) ||
          (o.sublabel ?? '').toLowerCase().includes(query.toLowerCase()) ||
          o.value.toLowerCase().includes(query.toLowerCase()),
      )
    : options

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Auto-focus search when opening
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, onBlur, value },
        fieldState: { error },
      }) => {
        const selected = options.find((o) => o.value === value)

        const handleSelect = (opt: ComboboxOption) => {
          onChange(opt.value)
          if (opt.value) {
            clearErrors(name)
          }
          // Immediate revalidation to clear required error state
          trigger(name)
          setOpen(false)
          setQuery('')
        }

        return (
          <div className={cn('flex flex-col gap-1.5', className)}>
            <label
              htmlFor={name}
              className={cn(
                'text-sm font-medium',
                isLight ? 'text-neutral-700' : 'text-neutral-300',
              )}
            >
              {label}
              {rules?.required && (
                <span className="ml-1 text-primary-500">*</span>
              )}
            </label>

            {/* Button + dropdown anchored together */}
            <div className="relative" ref={containerRef}>
              {/* Trigger button */}
              <button
                id={name}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-invalid={!!error}
                onClick={() => {
                  if (open) {
                    onBlur()
                    trigger(name)
                  }
                  setOpen((v) => !v)
                }}
                className={cn(
                  'relative flex h-12 w-full items-center gap-2 overflow-hidden rounded-xl border px-3.5 text-left text-sm transition-all duration-200',
                  'focus:outline-none',
                  isLight
                    ? [
                        'bg-white',
                        open
                          ? 'border-primary-500 ring-3 ring-primary-500/15'
                          : error
                            ? 'border-red-400 ring-2 ring-red-400/15'
                            : 'border-neutral-200 hover:border-neutral-300',
                      ]
                    : [
                        'bg-white/5',
                        open
                          ? 'border-primary-500/60 shadow-[0_0_0_3px_rgba(255,182,6,0.10)]'
                          : error
                            ? 'border-red-500/60 ring-2 ring-red-500/15'
                            : 'border-white/10 hover:border-white/20',
                      ],
                )}
              >
                {icon && (
                  <span
                    className={cn(
                      'shrink-0 text-base',
                      isLight ? 'text-neutral-400' : 'text-neutral-400',
                    )}
                  >
                    {icon}
                  </span>
                )}
                <span
                  className={cn(
                    'flex-1 truncate',
                    selected
                      ? isLight
                        ? 'font-medium text-neutral-900'
                        : 'font-medium text-white'
                      : isLight
                        ? 'text-neutral-400'
                        : 'text-neutral-500',
                  )}
                >
                  {selected ? selected.label : placeholder}
                </span>
                <LuChevronDown
                  className={cn(
                    'shrink-0 text-neutral-400 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                />
              </button>

              {/* Dropdown panel */}
              {open && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1.5 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl shadow-neutral-900/10 ring-1 ring-black/5 animate-in fade-in zoom-in-95 duration-150">
                  {/* Search input */}
                  <div className="flex items-center gap-2 border-b border-neutral-100 bg-neutral-50/50 px-3 py-2.5">
                    <LuSearch className="shrink-0 text-neutral-400 h-4 w-4" />
                    <input
                      ref={inputRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="flex-1 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 outline-none"
                    />
                  </div>

                  {/* Options list */}
                  <ul
                    role="listbox"
                    className="max-h-60 overflow-y-auto p-1.5 space-y-0.5"
                  >
                    {filtered.length === 0 ? (
                      <li className="px-3 py-3 text-center text-xs text-neutral-400">
                        No results found for "{query}"
                      </li>
                    ) : (
                      filtered.map((opt) => {
                        const isActive = opt.value === value
                        return (
                          <li
                            key={opt.value}
                            role="option"
                            aria-selected={isActive}
                            onClick={() => handleSelect(opt)}
                            className={cn(
                              'flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors duration-150',
                              isActive
                                ? 'bg-primary-50 font-semibold text-primary-900'
                                : 'text-neutral-700 hover:bg-neutral-100/80 hover:text-neutral-900',
                            )}
                          >
                            <span className="truncate flex items-center gap-2">
                              <span>{opt.label}</span>
                              {opt.sublabel && (
                                <span className="text-xs text-neutral-400 font-normal">
                                  ({opt.sublabel})
                                </span>
                              )}
                            </span>
                            {isActive && (
                              <LuCheck className="shrink-0 text-primary-600 h-4 w-4" />
                            )}
                          </li>
                        )
                      })
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error?.message && (
              <p
                role="alert"
                className={cn(
                  'text-xs font-medium',
                  isLight ? 'text-red-500' : 'text-red-400',
                )}
              >
                {error.message}
              </p>
            )}
          </div>
        )
      }}
    />
  )
}
